/**
 * 老师素材加载
 * ============================================================
 * 从 `public/teachers/manifest.json` 读取素材路径，**与代码解耦** ——
 * 换老师形象只需改 manifest 与图片，不用动组件。
 *
 * 为什么不用 import 静态资源：
 *   那样每次换素材都要改代码重新构建。放进 public/ + manifest
 *   可以做到"运营换图，开发不动"。
 */

import { assetUrl } from './assetUrl';

/*
 * 清单本身也要过 assetUrl —— 应用可能部署在子目录（如 /dialectgo/），
 * 绝对路径 `/teachers/...` 会指向域名根，清单请求直接 404，
 * 表现是"老师不出图、也没有任何报错"。
 */
const MANIFEST_URL = assetUrl('/teachers/manifest.json');

let cache = null; // 进程内缓存，避免每次进课堂都请求
let inflight = null;

/**
 * 读取素材清单
 * @returns {Promise<Object>} { tutors: { [id]: {...} } }
 */
export async function loadTeacherManifest() {
  if (cache) return cache;
  if (inflight) return inflight;

  /*
   * ⚠️ 这里**不能**用 `cache: 'force-cache'`。
   *
   * 原实现想用它「避免每次进课堂都请求」，但该选项的语义是
   * 「只要缓存里有就用，不验证是否过期」。后果是：
   * 运营更新了 manifest（例如新生成了一批口型片段）之后，
   * 老用户会**一直**读到旧清单 —— 表现为「片段明明生成了、前端也查不到，
   * 虚拟人还是一张静态图」。这个症状极难排查，因为服务器上的数据是对的、
   * 代码也是对的，只有客户端在用过期的表。
   *
   * 改用 'no-cache'：每次向服务器确认一次，配合 express.static 的 ETag
   * 通常命中 304（无 body），开销可忽略。而本模块自身已有内存缓存
   * （见上方 `cache` 变量），同一次会话内只真正请求一次，
   * 「每次进课堂都请求」的担心并不成立。
   */
  inflight = fetch(MANIFEST_URL, { cache: 'no-cache' })
    .then((res) => {
      if (!res.ok) throw new Error(`manifest HTTP ${res.status}`);
      return res.json();
    })
    .then((j) => {
      cache = j && typeof j === 'object' ? j : { tutors: {} };
      return cache;
    })
    .catch((e) => {
      // 素材加载失败**不能影响上课** —— 返回空清单，组件会走占位分支
      console.warn('[teacherAssets] 素材清单加载失败，使用占位：', e.message);
      cache = { tutors: {} };
      return cache;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/**
 * 取某个老师的素材
 *
 * @param {string} tutorId
 * @returns {Promise<{portrait:string|null, poster:string|null, video:string|null, lipSync:string, credit:Object|null}>}
 */
export async function getTeacherAsset(tutorId) {
  const m = await loadTeacherManifest();
  const a = m?.tutors?.[tutorId];
  if (!a) {
    return {
      name: null,
      portrait: null,
      poster: null,
      video: null,
      lipSync: 'none',
      mouthPosition: null,
      credit: null,
    };
  }
  return {
    /*
     * name 一定要透出。
     *
     * 老师形象现在是**按方言选**的（见 tutorClip.js#resolveTutorForDialect），
     * 而"人设"是用户自己选的 —— 两者可能不是同一个人。
     * 如果界面上的名字仍取自人设，就会出现「画面上是阿峰、名字写着阿清」。
     */
    name: a.name || null,
    /*
     * 下面这些路径原样写在 manifest 里（`/teachers/...`）。
     * 必须过 assetUrl 补上 base，否则子目录部署时全部 404。
     */
    portrait: assetUrl(a.portrait) || null,
    poster: assetUrl(a.poster) || null,
    video: assetUrl(a.video) || null,
    /*
     * idleVideo —— 没有口型片段时用的**静置循环素材**。
     *
     * 对话是 LLM 实时生成的文本，不可能预生成口型片段。
     * 但界面上一张死图也不像"老师在跟你说话"。
     * idle 素材是基准视频压出来的循环（有真实的眨眼与头部微动），
     * **嘴部不驱动** —— 不假装在说某句话，只是"人在这儿"。
     */
    idleVideo: assetUrl(a.idleVideo) || null,
    idlePoster: assetUrl(a.idlePoster) || null,
    videoAudioSync: a.videoAudioSync || null,
    lipSync: a.lipSync || 'none',
    // ⚠️ mouthPosition 必须透出，否则组件拿不到嘴部坐标，
    //    口型区域永远不会渲染 —— 这是"改了但没接上"的典型。
    mouthPosition: a.mouthPosition || null,
    lipSyncNote: a.lipSyncNote || null,
    credit: a.credit || null,
  };
}

/** 清缓存（测试与热更新用） */
export function _clearTeacherManifestCache() {
  cache = null;
  inflight = null;
}
